/**
 * 11-option operation picker.
 *
 * Renders a labelled group of checkboxes for every RBAC operation. The
 * `admin` operation carries a warning icon because checking it grants
 * admin-UI write access. Disabling is supported for read-only preview.
 */

import React, { useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Checkbox from '@amzn/meridian/checkbox';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import { TemplateDropdown, TemplateOption } from './TemplateDropdown';

export const ALL_OPERATIONS: Array<{ id: string; label: string; description: string; warn?: boolean }> = [
  { id: 'tm1_start', label: 'Start TM1 instances', description: 'Power on an EC2 instance hosting TM1.' },
  { id: 'tm1_stop', label: 'Stop TM1 instances', description: 'Gracefully shut down a TM1 EC2 instance.' },
  { id: 'tm1_backup', label: 'Backup TM1 instances', description: 'Trigger an AMI snapshot backup.' },
  { id: 'odbc_setup', label: 'Set up ODBC connections', description: 'Configure ODBC DSN on TM1 instances.' },
  { id: 's3_provision', label: 'Provision S3 buckets', description: 'Create S3 buckets with secure defaults.' },
  { id: 'scan', label: 'Run the instance scanner', description: 'Kick off a full inventory scan.' },
  { id: 'report_view', label: 'View infrastructure reports', description: 'Query the instance registry.' },
  { id: 'report_export', label: 'Export reports (CSV/Excel)', description: 'Download report data.' },
  { id: 'ticket_read', label: 'Read tickets', description: 'Fetch ticket content from Tickety.' },
  { id: 'ticket_escalate', label: 'Escalate tickets to engineering', description: 'Hand off to GFT Infra.' },
  {
    id: 'admin',
    label: 'Manage RBAC policies (admin UI)',
    description: 'Grants write access to this admin interface.',
    warn: true,
  },
];

export interface OperationCheckboxGroupProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  templates?: TemplateOption[];
  onSelectTemplate?: (name: string) => void;
}

export const OperationCheckboxGroup: React.FC<OperationCheckboxGroupProps> = ({
  value,
  onChange,
  disabled,
  templates,
  onSelectTemplate,
}) => {
  const selected = useMemo(() => new Set(value), [value]);

  const toggle = (opId: string) => {
    const next = new Set(selected);
    if (next.has(opId)) next.delete(opId);
    else next.add(opId);
    onChange(Array.from(next).sort());
  };

  const adminSelected = selected.has('admin');

  return (
    <Column spacing="small">
      {templates && templates.length > 0 && onSelectTemplate && (
        <Row alignmentVertical="center" spacing="medium">
          <Text type="b300">Start from template:</Text>
          <TemplateDropdown templates={templates} onSelect={onSelectTemplate} disabled={disabled} />
        </Row>
      )}
      {adminSelected && (
        <Alert type="warning" title="Admin capability enabled">
          Members of this group will be able to manage RBAC policies and user mappings. Make sure
          that is intentional.
        </Alert>
      )}
      <Column spacing="xsmall" tag="fieldset" aria-label="Allowed operations">
        {ALL_OPERATIONS.map((op) => (
          <Row key={op.id} alignmentVertical="top" spacing="small">
            <Checkbox
              checked={selected.has(op.id)}
              onChange={() => toggle(op.id)}
              disabled={disabled}
            >
              <Column spacing="none">
                <Text type="b300">
                  {op.label}
                  {op.warn ? ' ⚠️' : ''}
                </Text>
                <Text type="b200" color="secondary">
                  {op.description}
                </Text>
              </Column>
            </Checkbox>
          </Row>
        ))}
      </Column>
    </Column>
  );
};
